import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "./NearbyOrdersList.css";

const NearbyOrdersList = ({
  nearbyPoints,
  orderColors,
  selectedOrderId,
  setSelectedOrderId,
  selectedPoint,
}) => {
  const scrollContainerRef = useRef(null);
  const orderCardRefs = useRef({});

  // Función para aclarar el color base (reduce intensidad)
  const lightenColor = (hex, alpha = 0.18) => {
    if (!hex) return `rgba(255,255,255,${alpha})`;
    const bigint = parseInt(hex.replace("#", ""), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Efecto para hacer scroll hacia la orden seleccionada
  useEffect(() => {
    if (selectedOrderId && scrollContainerRef.current) {
      const selectedCard = orderCardRefs.current[selectedOrderId];
      if (selectedCard) {
        const container = scrollContainerRef.current;

        // Calcular la posición relativa al contenedor
        const cardRect = selectedCard.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const relativeTop =
          cardRect.top - containerRect.top + container.scrollTop;
        const cardHeight = selectedCard.offsetHeight;
        const containerHeight = container.clientHeight;

        // Calcular la posición para centrar la card en el contenedor
        const scrollPosition =
          relativeTop - containerHeight / 2 + cardHeight / 2;

        container.scrollTo({
          top: Math.max(0, scrollPosition),
          behavior: "smooth",
        });
      }
    }
  }, [selectedOrderId]);

  return (
    <div className="mt-3">
      <h6 className="fw-bold">Órdenes cercanas:</h6>
      <div
        ref={scrollContainerRef}
        style={{ maxHeight: "500px", overflowY: "auto" }}
      >
        {nearbyPoints.length > 0 ? (
          nearbyPoints.map((p) => {
            const orderColor = orderColors[p.order_id] || "#000000";
            const isSelected = selectedOrderId === p.order_id;
            const baseBg = isSelected
              ? lightenColor(orderColor, 0.25)
              : "#ffffff";

            return (
              <div
                key={p.order_id}
                ref={(el) => {
                  if (el) orderCardRefs.current[p.order_id] = el;
                }}
                className={`order-card p-3 rounded mb-3 shadow-sm ${
                  isSelected ? "animated-border" : ""
                }`}
                style={{
                  border: `3px solid ${orderColor}`,
                  borderRadius: "13px",
                  cursor: "pointer",
                  backgroundColor: baseBg,
                  boxShadow: isSelected
                    ? `0 0 10px ${orderColor}`
                    : "0 2px 6px rgba(0, 0, 0, 0.1)",
                  transition:
                    "transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
                  marginLeft: "6px",
                  marginRight: "6px",
                }}
                onClick={() =>
                  setSelectedOrderId(isSelected ? null : p.order_id)
                }
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.02)";
                  e.currentTarget.style.boxShadow = `0 0 12px ${orderColor}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = isSelected
                    ? `0 0 10px ${orderColor}`
                    : "0 2px 6px rgba(0, 0, 0, 0.1)";
                }}
              >
                <div>
                  <strong>Fecha reporte:</strong>{" "}
                  {p.fecha_hora_reporte
                    ? new Date(p.fecha_hora_reporte).toLocaleString("es-MX", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })
                    : "N/A"}
                </div>

                <div>
                  <strong>Folio:</strong>{" "}
                  {p.folio ? (
                    <a
                      href={`/orders/edit/${p.order_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        color: "#004aad",
                        textDecoration: "underline",
                        fontWeight: "bold",
                        transition: "color 0.2s ease, text-shadow 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = "#001052";
                        e.currentTarget.style.textShadow = `0 0 5px #57E4EB`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = "#004aad";
                        e.currentTarget.style.textShadow = "none";
                      }}
                    >
                      {p.folio.toString().padStart(5, "0")}
                    </a>
                  ) : (
                    <span
                      style={{
                        color: "#004aad",
                        textDecoration: "underline",
                        fontWeight: "bold",
                      }}
                    >
                      {p.order_id}
                    </span>
                  )}
                </div>

                <div>
                  <strong>Subtotal:</strong> ${Number(p.subtotal).toFixed(2)}
                </div>
                <div>
                  <strong>Total:</strong> ${Number(p.total).toFixed(2)}
                </div>
                <div>
                  <strong>Vehículo:</strong>{" "}
                  {p.vehicle
                    ? `${p.vehicle.model?.type?.name + " - " || "N/A"} ${
                        p.vehicle.model?.model_name || ""
                      } (${p.vehicle.anio || "N/A"})`
                    : "Sin vehículo"}
                </div>
                <div>
                  <strong>Distancia total:</strong>{" "}
                  {Number(p.totalDistance).toFixed(2)} km
                </div>
                <div>
                  <strong>Precio por km:</strong> $
                  {Number(p.precio_por_km).toFixed(2)}
                </div>
              </div>
            );
          })
        ) : (
          <p className="text-muted small">
            {selectedPoint
              ? "No hay puntos cercanos."
              : "Selecciona un punto para ver resultados."}
          </p>
        )}
      </div>
    </div>
  );
};

export default NearbyOrdersList;
